'use client';

import { ReactNode } from 'react';

import dynamic from 'next/dynamic';

import { motion, type Easing } from 'framer-motion';

import Background from '../sections/new-hero/background';

// Dynamically import AISphere, disable SSR as it likely needs browser APIs
const DynamicAISphere = dynamic(
  () => import('~/components/webgl/ai-sphere/ai-sphere'),
  {
    ssr: false,
    loading: () => (
      <div className="h-[500px] w-[500px] md:h-[730px] md:w-[730px]"></div>
    ),
  }, // Optional: Add a placeholder with same dimensions
);

const ease: Easing = [0.16, 1, 0.3, 1];

function HeroPill({ href, children }: { href?: string; children?: ReactNode }) {
  return (
    <motion.a
      href={href}
      // TODO: check ring-1 ring-accent => border
      className="relative z-30 flex w-auto max-w-1440 items-center space-x-2 rounded-[100px] p-0.5 pr-2.5 ring-1 ring-white/10"
      style={{
        background:
          'linear-gradient(180deg, rgba(0, 0, 0, 0.10) 0%, rgba(0, 0, 0, 0.00) 100%), #1E1D26',
      }}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease }}
    >
      <svg
        width="15"
        height="14"
        viewBox="0 0 15 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M7.49992 10.2085C9.72793 10.2085 10.7083 9.22817 10.7083 7.00016C10.7083 9.22817 11.6886 10.2085 13.9166 10.2085C11.6886 10.2085 10.7083 11.1888 10.7083 13.4168C10.7083 11.1888 9.72793 10.2085 7.49992 10.2085Z"
          stroke="white"
          strokeWidth="1.5"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M1.08325 5.25016C4.32399 5.25016 5.74992 3.82424 5.74992 0.583496C5.74992 3.82424 7.17584 5.25016 10.4166 5.25016C7.17584 5.25016 5.74992 6.67609 5.74992 9.91683C5.74992 6.67609 4.32399 5.25016 1.08325 5.25016Z"
          stroke="white"
          strokeWidth="1.5"
        />
      </svg>
      <p className="text-xs font-medium leading-6 tracking-[1.008px] text-content-primary-inverted dark:text-content-primary">
        {children}
      </p>
    </motion.a>
  );
}

function HeroTitles({
  heading = '',
  subHeading,
}: {
  heading?: string;
  subHeading?: ReactNode;
}) {
  return (
    <div className="relative z-30 mx-auto mt-2.5 flex w-full max-w-2xl flex-col space-y-4 overflow-hidden lg:mt-4">
      <motion.h1
        className="flex flex-wrap justify-center gap-3 gap-y-0 text-[56px] font-medium leading-[60px] tracking-[-0.48px] lg:text-[56px] lg:tracking-[-0.56px]"
        initial={{ filter: 'blur(10px)', opacity: 0, y: 50 }}
        animate={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
        transition={{
          duration: 1,
          ease,
          staggerChildren: 0.2,
        }}
      >
        {heading?.split(/\s+/i).map((text, index) => (
          <motion.span
            key={index}
            className="inline-block text-balance font-semibold leading-[1.2] tracking-[0.1px] lg:leading-[60px] lg:tracking-normal"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.8,
              delay: index * 0.2,
              ease,
            }}
          >
            {text}
          </motion.span>
        ))}
      </motion.h1>
      <motion.p
        className="mx-auto !mt-8 max-w-[510px] text-center text-[20px] leading-[1.4] text-content-secondary"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          delay: 0.6,
          duration: 0.8,
          ease,
        }}
      >
        {subHeading}
      </motion.p>
    </div>
  );
}

export default function Hero({
  heading,
  subHeading,
  tag: { label, url } = {},
}: {
  heading?: string;
  subHeading?: ReactNode;
  tag?: { label?: string; url?: string };
}) {
  return (
    <section
      id="hero"
      className="module-hero relative mx-auto flex w-full flex-col items-center justify-start overflow-hidden px-6 pt-[137px] lg:px-8 lg:pt-36"
    >
      <HeroPill href={url}>{label}</HeroPill>
      <div className="max-h-[800px] w-full overflow-hidden">
        <HeroTitles heading={heading} subHeading={subHeading} />
        <div className="relative -top-12 flex w-full justify-center">
          <div className="w-[500px]md:h-[730px] relative h-[500px] md:w-[730px]">
            <DynamicAISphere
              className="relative z-30 h-full w-full"
              variant="entry"
              isScrollElementOnly
            />
          </div>
        </div>
      </div>
      <div
        className="pointer-events-none absolute bottom-0 left-0 z-40 h-[30%] w-full lg:h-[16%]"
        style={{
          background:
            'linear-gradient(180deg, rgba(5, 5, 10, 0.00) 0%, #05050A 100%)',
        }}
      ></div>
      <Background />
    </section>
  );
}
